import { actionChannel, delay, fork, put, take, takeLatest, takeLeading, select, call } from "@redux-saga/core/effects"
import config from "react-global-configuration"
import {
  getCurrentSelectedPatientId,
  getDoctorTypeAnswer,
} from "containers/Survey/services/selector"
import {
  SUBMIT_DOCUMENT_ONGOING,
  SUBMIT_DOCUMENT_REQUEST,
  SEND_ME_UPLOAD_LINK_REQUEST,
  SEND_ME_UPLOAD_LINK_VIA_SMS_REQUEST,
  FETCH_NEW_DOCUMENT_REQUEST,
  ADD_DERMATO_PHOTO,
  ADD_DOCUMENTS_TO_STORE,
  RESET_UPLOAD_STORE,
  QR_CODE_LINK_SUCCESS,
  FETCH_DERMATO_PATIENT_DETAILS_REQUEST,
  MAIN_PATIENT_ID,
} from "./constants"
import { getCustomer } from "client/services/selector"
import { RESET_STORE } from "client/services/constants"
import {
  customerIdentity,
  persistUploadSnapshot,
} from "containers/Survey/services/surveyStorage"
import { UploadDocStore, UploadLink } from "types/store"
import { Action } from "types/redux"
import { savePdf } from "../utils"
import actions, { FetchNewDocumentsRequest, SmsLinkRequest, UploadLinkRequest } from "./actions"
import { isThisDocNewlyUpdated } from "containers/Survey/utils"
import { request } from "lib/request"
import { handleGenericApiError } from "lib/api-errors"
import { ERROR_API } from "types/error"
import { PDFDocument } from "pdf-lib"
import {
  DERMATOSCOPE_VIDEO_PAGE,
  DERMATO_RECAP_PAGE,
  TELEEXPERTISE_CONSENT_PAGE,
} from "core/constants"
import { DoctorType } from "types/entity"
import { GET_ALL_DOCUMENT_REQUESTS, SET_QUESTION } from "containers/Survey/services/constants"
import { ADD_PHONE_PHOTO } from "containers/Upload/services/constants"
import { PATH } from "containers/Survey/models"
import { languages } from "locales/languages"
import {
  filterDocsForSelectedPatient,
  getUploadRelativeId,
  withRelativeIdQuery,
} from "./utils"

// Aligned with nginx client_max_body_size (10M): reject earlier with a clear
// message instead of an opaque 413 after the whole upload.
const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024
// A multi-page PDF from a kiosk on a slow link legitimately takes longer than
// the 10s of the shared request() wrapper.
const UPLOAD_TIMEOUT_MS = 60 * 1000

function sanitizeFilename(filename: string): string {
  const base = (filename || "document").trim() || "document"
  return base
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
}

function createFile(
  filename: string,
  fileContent: Blob | Uint8Array,
  type: string,
  relativeId?: string | number,
) {
  const sanitized = sanitizeFilename(filename)
  const safeFilename = sanitized.endsWith(".pdf")
    ? sanitized
    : `${sanitized}.pdf`
  const myFile = new File([fileContent], safeFilename, {
    type: "application/pdf",
  })
  if (myFile.size > MAX_UPLOAD_SIZE_BYTES) {
    throw new Error(languages.uploadDocumentTooLarge)
  }
  const file = new FormData()
  file.append("file", myFile)
  file.append("type", type)
  if (relativeId !== undefined && relativeId !== null && relativeId !== "") {
    file.append("relative_id", String(relativeId))
  }
  return file
}

async function handleUploadApiError(response: Response): Promise<never> {
  let message = languages.defaultErrorForm
  try {
    const body = await response.json()
    if (typeof body?.message === "string" && body.message) {
      message = body.message
    } else if (typeof body?.error === "string" && body.error) {
      message = body.error
    }
  } catch {
    // keep default message
  }
  throw new Error(message)
}

// Direct fetch (multipart body): the shared request() wrapper JSON-encodes its
// payload, so the generic error handling and a timeout are applied here.
async function postCustomerDocument(fileData: FormData) {
  const token = sessionStorage.getItem("X-USER-Token") || ""
  const route = config.get("patient.uploadDocument") ?? "/documents"
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS)
  let response: Response
  try {
    response = await fetch(`/api${route}`, {
      method: "POST",
      headers: { "X-User-Token": token },
      body: fileData,
      signal: controller.signal,
    })
  } finally {
    clearTimeout(timeout)
  }
  if (!response.ok) {
    // Same behaviour as request(): 401 logs out, maintenance redirects,
    // forbidden pharmacy purges the installation. Other statuses fall
    // through to the upload-specific message.
    if (
      response.status === ERROR_API.UNAUTHORIZED ||
      response.status === ERROR_API.MAINTENANCE ||
      response.status === ERROR_API.FORBIDDEN ||
      response.status === ERROR_API.EXPIRED_TOKEN ||
      response.status === ERROR_API.DELETED_INSTALLATION ||
      response.status === ERROR_API.EXPIRED_TOKEN_PHARMACY
    ) {
      await handleGenericApiError(response)
    }
    return handleUploadApiError(response)
  }
  return response.json()
}

function* requestLink(action: Action<UploadLinkRequest>) {
  const { path: uploadPath, standalone } = action?.payload ?? { standalone: false }
  // Outside the survey (Profile > Documents importés) there is no selected
  // patient: the link is issued for the account holder.
  const currentCustomerId = standalone
    ? MAIN_PATIENT_ID
    : yield select(getCurrentSelectedPatientId)
  const url = withRelativeIdQuery(
    config.get("patient.uploadLink"),
    currentCustomerId,
    { path: uploadPath },
  )

  yield put({
    type: SUBMIT_DOCUMENT_ONGOING,
  })
  // reset number of new doc since last link asked
  localStorage.removeItem("NDC");
  localStorage.removeItem("RNDC");
  yield put(actions.clearProcessedPhonePhotoDocs())
  // The consumed dermato details are deliberately NOT cleared here: the QR
  // link is regenerated several times during one TLE (expiry, resetStatus
  // after a dermatoscope upload, remount) and clearing the consumed document
  // id would let the next poll re-accept the same submission and jump to the
  // dermatoscope step again. They are reset with RESET_UPLOAD_STORE (start of
  // TLE, patient/relative change); the 15-minute filter in
  // fetchDermatoPatientDetails covers a document left by a previous TLE.
  try {
    const response = yield request(url, {
      method: "GET",
    })
    if (response.status === "ok") {
      yield put(
        actions.QrcodeLinkSuccess({
          url: response.link,
          created_at: response?.created_at || new Date(),
          // Fallback in the future: a past date made the QR page ask for a
          // new link every 30s when the API omitted end_at.
          end_at: response?.end_at || new Date(Date.now() + 15 * 60 * 1000),
          received_at: Date.now(),
          SendBySms: false,
          patientId: currentCustomerId,
          isDermato: uploadPath === "dermato",
        }),
      )
    }
  } catch (err) {
    console.error(err, {
      route: url,
    })
    yield put(actions.failure(err))
  }
}

function* sendLinkViaSms(action: Action<SmsLinkRequest>) {
  const { isDermato, standalone } = action?.payload ?? {}
  // Same patient as the QR link: the account holder outside the survey.
  const currentCustomerId = standalone
    ? MAIN_PATIENT_ID
    : yield select(getCurrentSelectedPatientId)
  yield put({
    type: SUBMIT_DOCUMENT_ONGOING,
  })

  try {
    const url = withRelativeIdQuery(
      config.get("patient.linkBySms"),
      currentCustomerId,
      { is_dermato: isDermato ? "true" : undefined },
    )
    const response = yield request(url, {
      method: "GET",
    })
    if (response.status === "ok") {
      yield put(actions.linkViaSmsSuccess())
    }
  } catch (err) {
    console.error(err, {
      route: config.get("patient.linkBySms"),
    })
    yield put(actions.failure(err))
  }
}

function* uploadFileData(fileData) {
  try {
    const uploadedDocument = yield postCustomerDocument(fileData)
    const doctorType = yield select(getDoctorTypeAnswer)
    if (doctorType === DoctorType.DERMATOLOGY) {
      if (uploadedDocument?.id) {
        yield put(actions.markPhonePhotoDocsProcessed([uploadedDocument.id]))
        yield put({
          type: SET_QUESTION,
          payload: {
            id: PATH.selected_documents,
            replies: [{ value: uploadedDocument.id }],
          },
        })
      }
    }
    yield put(actions.success())
  } catch (e) {
    console.error(e, {
      route: config.get("patient.uploadDocument"),
    })
    yield put(actions.failure(e))
  }
}

function* uploadDocument(action) {
  const currentCustomerId = yield select(getCurrentSelectedPatientId)
  const { name, file, medicalType } = action.payload

  if (!file?.length) {
    yield put(actions.failure(new Error(languages.defaultErrorForm)))
    return
  }

  const pdffile = yield savePdf(file)

  yield put({
    type: SUBMIT_DOCUMENT_ONGOING,
  })

  let filedata: FormData
  try {
    filedata = createFile(
      name || "document",
      pdffile,
      medicalType,
      getUploadRelativeId(currentCustomerId),
    )
  } catch (error) {
    yield put(actions.failure(error))
    return
  }

  yield uploadFileData(filedata)
}

// Each photo carries a stable "<docId>:<page>" key so the reducer can drop a
// document that gets downloaded twice (blob URLs are unique, so they cannot be
// used for deduplication).
function* splitPdfIntoPageBlobUrls(blob: Blob, docId: string | number) {
  if (!blob.type.includes("pdf")) {
    yield put(actions.addPhonePhoto(URL.createObjectURL(blob), `${docId}:0`))
    return
  }
  const arrayBuffer: ArrayBuffer = yield call(() => blob.arrayBuffer())
  const srcDoc = yield call(() => PDFDocument.load(arrayBuffer))
  const pageCount: number = srcDoc.getPageCount()
  for (let i = 0; i < pageCount; i++) {
    const pageDoc = yield call(() => PDFDocument.create())
    const [copiedPage] = yield call(() => pageDoc.copyPages(srcDoc, [i]))
    pageDoc.addPage(copiedPage)
    const pageBytes: Uint8Array = yield call(() => pageDoc.save())
    const pageBlob = new Blob([pageBytes], { type: "application/pdf" })
    const blobUrl = URL.createObjectURL(pageBlob)
    yield put(actions.addPhonePhoto(blobUrl, `${docId}:${i}`))
  }
}

function* fetchPhonePictureBlobUrls(newDocs: any[]) {
  const pictureDocs = newDocs.filter((d) => d.url)
  for (const doc of pictureDocs) {
    try {
      const blob: Blob = yield call(() =>
        request(doc.url, { method: "GET", responseFormat: "blob" }),
      )
      if (blob instanceof Blob) {
        yield* splitPdfIntoPageBlobUrls(blob, doc.id)
      }
    } catch (e) {
      console.error("Failed to fetch phone photo for recap", doc.url, e)
    }
  }
}

function* processNewDermatoDocs(docs: any[]) {
  const processedIds: Array<string | number> = yield select(
    (state) => state.upload?.processedPhonePhotoDocIds ?? [],
  )
  const unprocessed = docs.filter(
    (doc) =>
      doc?.id != null &&
      !processedIds.some((id) => String(id) === String(doc.id)),
  )
  if (unprocessed.length === 0) return

  // Mark before download so a later poll cannot re-queue the same docs.
  yield put(
    actions.markPhonePhotoDocsProcessed(unprocessed.map((doc) => doc.id)),
  )
  yield put({ type: GET_ALL_DOCUMENT_REQUESTS })
  yield* fetchPhonePictureBlobUrls(unprocessed)
  yield put({
    type: SET_QUESTION,
    payload: {
      id: PATH.selected_documents,
      replies: unprocessed.map((doc) => ({ value: doc.id })),
    },
  })
}

function* fetchNewDocument(action: Action<FetchNewDocumentsRequest>) {
  const { linkCreatedAt, standalone } = action.payload
  // Standalone upload (Profile > Documents importés): the documents belong to
  // the account holder and the survey answers are irrelevant, so neither the
  // "survey lost" redirect nor the dermato processing applies.
  const currentCustomerId = standalone
    ? MAIN_PATIENT_ID
    : yield select(getCurrentSelectedPatientId)
  const doctorType = standalone ? undefined : yield select(getDoctorTypeAnswer)
  try {
    const response = yield request(
      `${config.get("patient.uploadedDocuments")}`,
      {
        method: "GET",
      },
    )
    const CreatedRecently = response.filter((doc) =>
      isThisDocNewlyUpdated(doc, linkCreatedAt),
    )
    if (currentCustomerId === undefined) {
      yield put(actions.goBackToSurvey())
      return
    }
    // New-document counters: NDC for the main patient, RNDC for a relative.
    const counterKey = currentCustomerId === 0 ? "NDC" : "RNDC"
    const newDocs = filterDocsForSelectedPatient(
      CreatedRecently,
      currentCustomerId,
    )
    const knownCount: number = parseInt(localStorage.getItem(counterKey)) || 0
    if (newDocs.length > knownCount) {
      localStorage.setItem(counterKey, String(newDocs.length))
      yield put(actions.newDocsAreAvailable(newDocs.length))
    }
    if (doctorType === DoctorType.DERMATOLOGY) {
      yield* processNewDermatoDocs(newDocs)
    }
  } catch (err) {
    console.error(err, {
      route: `${config.get("patient.uploadedDocuments")}`,
    })
  }
}

// Mirror the uploaded documents and captured photos in IndexedDB so a window
// reload restores them alongside the survey snapshot. Persisting reads photo
// blobs asynchronously, so the writes are serialized through a channel to
// keep the latest state from being overwritten by a slower, older write.
function* syncUploadStorage() {
  const upload = yield select(
    ({ upload }: { upload: Partial<UploadDocStore> }) => upload,
  )
  const customer = yield select(getCustomer)
  yield call(persistUploadSnapshot, upload || {}, customerIdentity(customer))
}

function* watchUploadStorage() {
  const channel = yield actionChannel([
    ADD_DERMATO_PHOTO,
    ADD_PHONE_PHOTO,
    ADD_DOCUMENTS_TO_STORE,
    RESET_UPLOAD_STORE,
  ])
  while (true) {
    yield take(channel)
    yield call(syncUploadStorage)
  }
}

const DERMATO_DETAILS_MAX_AGE_SECONDS = 900
// Stale documents are logged once per id, not on every 5 s poll
let lastStaleDermatoDetailsId: string | null = null

// Staleness comes from the API's age_seconds (kiosk clocks can run ahead of the
// server); the document's created_at against the local clock is only a fallback
// for an API that does not send it. Absent both, the document is accepted.
const isStaleDermatoPatientDetails = (
  id: string | number,
  ageSeconds: unknown,
  docTime: number | null,
  route: string,
): boolean => {
  const stale =
    typeof ageSeconds === "number"
      ? ageSeconds > DERMATO_DETAILS_MAX_AGE_SECONDS
      : docTime != null &&
        !Number.isNaN(docTime) &&
        Date.now() - docTime > DERMATO_DETAILS_MAX_AGE_SECONDS * 1000
  if (stale && lastStaleDermatoDetailsId !== String(id)) {
    lastStaleDermatoDetailsId = String(id)
    console.error(
      `Dermato patient details ${id} ignored as stale (age_seconds=${ageSeconds ?? "n/a"})`,
      { route },
    )
  }
  return stale
}

function* fetchDermatoPatientDetails() {
  const currentCustomerId = yield select(getCurrentSelectedPatientId)
  const existingId = yield select(
    (state) => state.upload?.dermatoPatientDetailsId ?? null,
  )
  const existingCreatedAt: number | null = yield select(
    (state) => state.upload?.dermatoPatientDetailsCreatedAt ?? null,
  )
  const url = withRelativeIdQuery(
    config.get("patient.dermatoPatientDetails"),
    currentCustomerId,
  )

  try {
    const response = yield request(url, { method: "GET" })
    if (response?.status !== "ok" || !response?.data) return

    const ageSeconds: unknown = response.data.age_seconds
    const { id, dermato_patient_details, created_at: docCreatedAt } = response.data
    if (!dermato_patient_details) return

    const utcStr = docCreatedAt && !/Z|[+-]\d{2}:\d{2}$/.test(docCreatedAt)
      ? `${docCreatedAt}Z`
      : docCreatedAt
    const parsedTime = utcStr ? new Date(utcStr).getTime() : null
    const docTime = parsedTime != null && !isNaN(parsedTime) ? parsedTime : null

    // Ignore the document already consumed by this TLE, unless the API
    // re-created it since (newer created_at). Coming back to the QR page must
    // not re-trigger the jump to the next step on the same submission.
    if (existingId != null && String(existingId) === String(id)) {
      const recreated =
        docTime != null && existingCreatedAt != null && docTime > existingCreatedAt
      if (!recreated) return
    }

    if (isStaleDermatoPatientDetails(id, ageSeconds, docTime, url)) return

    yield put(
      actions.setDermatoPatientDetails({
        id: String(id),
        text: String(dermato_patient_details),
        receivedAt: Date.now(),
        createdAt: docTime,
      }),
    )
  } catch (err) {
    console.error(err, {
      route: url,
    })
  }
}

// Photos and patient details sent by the phone can still arrive after "Envoyer"
// has moved the kiosk past the QR screen. Keep the original link's
// context even when a dermatoscope upload clears upload.link via resetStatus.
// A new link or an upload/session reset cancels this poll through takeLatest.
function* pollDermatoUploads({ payload: link }: Action<UploadLink>) {
  if (!link?.isDermato) return

  while (true) {
    const patientId = yield select(getCurrentSelectedPatientId)
    const doctorType = yield select(getDoctorTypeAnswer)
    const sent = yield select((state) => state.payment?.teleexpertiseSent)
    if (
      patientId === undefined ||
      link.patientId !== patientId ||
      doctorType !== DoctorType.DERMATOLOGY ||
      sent
    ) return

    const pathname = yield select((state) => state.router?.location?.pathname)
    if ([
      DERMATOSCOPE_VIDEO_PAGE,
      DERMATO_RECAP_PAGE,
      TELEEXPERTISE_CONSENT_PAGE,
    ].includes(pathname)) {
      yield put(actions.fetchNewDocuments(link.created_at))
      yield put(actions.fetchDermatoPatientDetails())
    }
    yield delay(5000)
  }
}

export default function* rootSaga() {
  yield takeLatest(SUBMIT_DOCUMENT_REQUEST, uploadDocument)
  yield takeLatest(SEND_ME_UPLOAD_LINK_REQUEST, requestLink)
  yield takeLatest(SEND_ME_UPLOAD_LINK_VIA_SMS_REQUEST, sendLinkViaSms)
  yield takeLeading(FETCH_NEW_DOCUMENT_REQUEST, fetchNewDocument)
  yield takeLeading(FETCH_DERMATO_PATIENT_DETAILS_REQUEST, fetchDermatoPatientDetails)
  yield takeLatest([QR_CODE_LINK_SUCCESS, RESET_UPLOAD_STORE, RESET_STORE], pollDermatoUploads)
  yield fork(watchUploadStorage)
}
